import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { avatarSrc } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserCog, Camera, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [committee, setCommittee] = useState("");
  const [picture, setPicture] = useState(null);
  const [committees, setCommittees] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setCommittee(user.committee || "");
      setPicture(user.picture || null);
    }
  }, [user]);

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data.committees || [])).catch(() => {});
  }, []);

  const initials = (name || "U").replace(/^soror\s+/i, "").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Image must be under 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(data.path);
      toast.success("Photo uploaded — remember to save");
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, committee, picture });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8" data-testid="profile-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <UserCog className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">My Profile</h1>
          <p className="text-sm text-muted-foreground">Update your name, committee, and photo.</p>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex flex-col items-center gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
          <Avatar className="h-20 w-20">
            <AvatarImage src={avatarSrc(picture)} />
            <AvatarFallback className="bg-primary/10 text-lg font-semibold text-primary">{initials}</AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="profile-photo-input" />
            <Button variant="outline" size="sm" className="rounded-full" disabled={uploading}
              onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn">
              {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Camera className="mr-1.5 h-4 w-4" />}
              {uploading ? "Uploading…" : "Change photo"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">JPG, PNG or WEBP. Max 15MB.</p>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <Label htmlFor="profile-name">Full name</Label>
            <Input id="profile-name" data-testid="profile-name-input" value={name}
              onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="profile-email">Email</Label>
            <Input id="profile-email" value={user?.email || ""} disabled className="mt-1.5 bg-muted/50" />
            <p className="mt-1 text-xs text-muted-foreground">Email is used to sign in and can't be changed here.</p>
          </div>
          <div>
            <Label>Committee</Label>
            <Select value={committee || "none"} onValueChange={(v) => setCommittee(v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="profile-committee-select">
                <SelectValue placeholder="Select a committee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No committee</SelectItem>
                {committees.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`committee-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="profile-save-btn">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}
